import { useEffect } from "react"
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom"

import useRedirectOnBeIdChange from '../../../hooks/useRedirectOnBeIdChange.js'
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'

import AddGroupsModal from "./components/AddGroupsModal.jsx"
import AddRolesModal from "./components/AddRolesModal.jsx"
import AdministrationTabs from '../components/AdministrationTabs.jsx'
import CopyRowAsJSON from "../../../components/CopyRowAsJSON.jsx"
import Icon from "../../../components/Icon.jsx"
import Loadable from "../../../components/Loadable.jsx"
import PageTitle from "../../../components/PageTitle.jsx"
import TableColumnHeader from '../../../components/TableColumnHeader.jsx'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { NotificationTypes, addNotification } from "../../../state/actions/notificationActions.js"
import { fetchUser, fetchUserRoles, fetchUserGroups, addUserToGroup, removeUserFromGroup, deleteUserRole } from "../../../state/actions/userActions.js"

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal, businessEntityDictionarySignal } from "../../../state/signals/businessEntitySignals.js"
import { roleDictionarySignal } from "../../../state/signals/roleSignals.js"
import { userSignal, userLoadedSignal, userErrorSignal, userGroupsSignal, userGroupsLoadedSignal, userRolesSignal, userRolesLoadedSignal, userGroupsErrorSignal, userRolesErrorSignal } from "../../../state/signals/userSignals.js"


export default function EditUserRoles() {

    const navigate = useNavigate()
    const { beId, userId } = useParams()
    useRedirectOnBeIdChange(beId, `/be/${beId}/management/users`)
    const { userPermissionsSummary, config, accessToken, debug, accordionData } = useOutletContext()
    const pageTabSignal = useSignal("roles")

    useEffect(() => {
        fetchUser(userId, config, accessToken)
        fetchUserGroups(userId, config, accessToken, true)
        fetchUserRoles(userId, config, accessToken)
    }, [userId, config, accessToken])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUsers
    })

    const showModalAddGroupsSignal = useSignal(false)
    const groupsToAddToUserSignal = useSignal([])
    const showModalAddRolesSignal = useSignal(false)

    const showModalAddGroups = function () {
        //console.log("Showing new group modal")
        showModalAddGroupsSignal.value = true
    }

    const closeModalAddGroups = function () {
        showModalAddGroupsSignal.value = false
    }

    const addGroupToUser = async () => {
        closeModalAddGroups()
        //console.log(groupsToAddToUserSignal.value)
        await addUserToGroup(userId, groupsToAddToUserSignal.value, config, accessToken).then(
            () => {
                addNotification(`User added to group successfully`, NotificationTypes.SUCCESS, false, 'Add User to Group Succeeded')
            }
        ).catch(
            (error) => {
            console.error("Error adding group to user: ", error)
            addNotification(`Failed to add user to group. Please try again.`, NotificationTypes.ERROR, false, 'Add User to Group Failed')
            }
        )
    }

    const showModalAddRoles = function () {
        //console.log("Showing new roles modal")
        showModalAddRolesSignal.value = true
    }

    const closeModalAddRoles = function () {
        showModalAddRolesSignal.value = false
    }

    const handleRemoveGroup = async (group) => {
        //console.log("Removing group from user: ", group)
        const groupName = group.name
        await removeUserFromGroup(group.userId, group.id, config, accessToken).then(
            () => {
                addNotification(`User removed from group ${groupName} successfully`, NotificationTypes.SUCCESS, false, 'Remove User from Group Succeeded')
            }
        ).catch(
            (error) => {
                console.error("Error removing group from user: ", error)
                addNotification(`Failed to remove group ${group.name} from user ${userSignal.value.username}. Please try again.`, NotificationTypes.ERROR, false, 'Remove User from Group Failed')
            }
        )
    }

    const handleRemoveRole = async (role) => {
        //console.log("Removing role from user: ", role)
        const roleName = role.roleName
        await deleteUserRole(role.userId, role.roleId, {}, config, accessToken).then(
            () => {
                addNotification(`User removed from role ${roleName} successfully`, NotificationTypes.SUCCESS, false, 'Remove Role Succeeded')
            }
        ).catch(
            (error) => {
                console.error("Error removing role from user: ", error)
                addNotification(`Failed to remove role ${role.roleName} from user ${userSignal.value.username}. Please try again.`, NotificationTypes.ERROR, false, 'Remove Role Failed')
            }
        )
    }

    function GroupsTableRows({ items }) {
        if (!items || !items.length) {
            return <tr><td colSpan="9" className="text-center">No Groups to Display</td></tr>
        }
        const rows = items.map((item, idx) => (
            <GroupsTableRow item={item} key={idx} />
        ))
        return rows
    }

    const commaSeparateRoles = (group) => {
        const f = "commaSeparateRoles()"
        if (!('roleIds' in group) || group.roleIds.length == 0) {
            return null
        }
        let roleNames = []
        for (let i = 0; i < group.roleIds.length; i++) {
            if (!roleDictionarySignal.value) {
                const err = "Role dictionary signal has no value"
                console.error(err)
                if (userGroupsLoadedSignal.value == true) {
                    console.log("User groups were loaded")
                }else{
                    console.log("User groups were NOT loaded")
                }
                throw new Error(err)
            } else if (!(group.roleIds[i] in roleDictionarySignal.value)) {
                console.error(`${f}: the role dictionary doesn't know about a role with ID "${group.roleIds[i]}"`)
                roleNames.push(`Unidentified Role ${group.roleIds[i]}`)
                continue
            } else if (!("name" in roleDictionarySignal.value[group.roleIds[i]])) {
                console.error(`${f}: Unnamed group with ID ${group.roleIds[i]}`)
                roleNames.push(`Unidentified Role ${group.roleIds[i]}`)
                continue
            }
            roleNames.push(roleDictionarySignal.value[group.roleIds[i]].name)
        }
        return roleNames.join(', ')
    }

    const commaSeparateBusinessEntityNames = (item) => {
        if (!('businessEntityIds' in item) || item.businessEntityIds.length == 0) {
            return null
        }
        let beNames = []
        for (let i = 0; i < item.businessEntityIds.length; i++) {
            beNames.push(businessEntityDictionarySignal.value[item.businessEntityIds[i]].name)
        }
        return beNames.join(', ')
    }

    function GroupsTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON item={item} />
                            <Dropdown.Item onClick={
                                () => {
                                    handleRemoveGroup(item)
                                }
                            }>
                                <Icon icon="trash" width={16} height={16} />
                                &nbsp;Remove Group
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.name}</td>
                <td>{commaSeparateRoles(item)}</td>
                <td>{commaSeparateBusinessEntityNames(item)}</td>
                <td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
            </tr>
        )
    }

    function RolesTableRows({ items }) {
        if (!items || !items.length)
            return <tr><td colSpan="9" className="text-center">No Roles to Display</td></tr>

        const rows = items.map((item, idx) => (
            <RolesTableRow item={item} key={idx} />
        ))
        return rows
    }

    function RolesTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON item={item} />
                            <Dropdown.Item onClick={
                                () => {
                                    handleRemoveRole(item)
                                }
                            }>
                                <Icon icon="trash" width={16} height={16} />
                                &nbsp;Remove Role
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.roleName}</td>
                <td>{commaSeparateBusinessEntityNames(item)}</td>
                <td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
            </tr>
        )
    }

    return (
        <>
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users` }}>Users</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users/${userId}/` }}>{userSignal.value ? userSignal.value.username : "Loading..."}</Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/users/${userId}/roles` }} active>Roles</Breadcrumb.Item>
            </Breadcrumb>
            <PageTitle title='User Roles' canEditSignal={canEditComputed} />
            <AdministrationTabs activeKey="4" />
            <Form id="userdata">
                <Form.Group as={Row} className="mb-3" controlId="UserUsername">
                    <Form.Label column sm="2" className="text-end">Username:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserUsername" plaintext readOnly defaultValue={userSignal?.value?.username} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserFirstName">
                    <Form.Label column sm="2" className="text-end">First Name:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserFirstName" plaintext readOnly defaultValue={userSignal?.value?.firstName} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserLastName">
                    <Form.Label column sm="2" className="text-end">Last Name:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserLastName" plaintext readOnly defaultValue={userSignal?.value?.lastName} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UserEmail">
                    <Form.Label column sm="2" className="text-end">Email Address:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[userLoadedSignal]} errorSignals={[userErrorSignal]}>
                            <Form.Control name="UserEmail" plaintext readOnly defaultValue={userSignal?.value?.email} />
                        </Loadable>
                    </Col>
                </Form.Group>
            </Form>

            <Nav
                variant="tabs"
                size="sm"
                className="px-3 my-3"
                activeKey={pageTabSignal.value}
            >
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}`} eventkey="profile" className="nav-link">Profile</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/roles`} eventkey="roles" className="nav-link active">Roles</Link>
                </Nav.Item>
                <Nav.Item>
                    <Link to={`/be/${beId}/management/users/${userId}/permissions`} eventkey="permissions" className="nav-link">Permissions</Link>
                </Nav.Item>
            </Nav>

            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item><h2 className="display-4 fs-4 px-3">Groups</h2></Nav.Item>
                {canEditComputed.value &&
                    <Nav.Item className="mt-1 me-2">
                        <Button size="sm" variant="outline-secondary"
                            className="d-flex align-items-center justify-content-center"
                            onClick={showModalAddGroups}>
                            <Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;Add to Group</Button>
                    </Nav.Item>}
            </Nav>
            <Loadable loadedSignals={[userGroupsLoadedSignal]} errorSignals={[userGroupsErrorSignal]}>
                {/*Note: This table as well as the one below must be a vanilla HTML table and not a react boostrap Table in order to attach the overflow-y: none CSS property, which prevents the dropdown menu for the last element in the list from stretching the table or getting cut off*/}
                <table className="cc-table2 table table-sm table-striped table-hover" style={{ overflowY: "show" }}>
                    <thead>
                        <tr>
                            <TableColumnHeader title={'Action'} datafield={'action'}></TableColumnHeader>
                            <TableColumnHeader title={'Name'} datafield={'name'}>Name</TableColumnHeader>
                            <TableColumnHeader title={'Roles'} datafield={'roles'}>Roles</TableColumnHeader>
                            <TableColumnHeader title={'Accounts'} datafield={'accounts'}>Accounts</TableColumnHeader>
                            <TableColumnHeader title={'Added'} datafield={'added'}>Added</TableColumnHeader>
                        </tr>
                    </thead>
                    <tbody><GroupsTableRows items={userGroupsSignal.value} /></tbody>
                </table>
            </Loadable>
            <hr></hr>

            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item><h2 className="display-4 fs-4 px-3">Roles</h2></Nav.Item>
                {canEditComputed.value &&
                    <Nav.Item className="mt-1 me-2">
                        <Button size="sm" variant="outline-secondary"
                            className="d-flex align-items-center justify-content-center"
                            onClick={showModalAddRoles}>
                            <Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;Add Role</Button>
                    </Nav.Item>}
            </Nav>

            <Loadable loadedSignals={[userRolesLoadedSignal]} errorSignals={[userRolesErrorSignal]}>
                <table className="cc-table2 table table-sm table-striped table-hover" style={{ overflowY: "show" }}>
                    <thead>
                        <tr>
                            <TableColumnHeader title={'Action'} datafield={'action'}></TableColumnHeader>
                            <TableColumnHeader title={'Name'} datafield={'name'}>Name</TableColumnHeader>
                            <TableColumnHeader title={'Accounts'} datafield={'accounts'}>Accounts</TableColumnHeader>
                            <TableColumnHeader title={'Added'} datafield={'added'}>Added</TableColumnHeader>
                        </tr>
                    </thead>
                    <tbody><RolesTableRows items={userRolesSignal.value} /></tbody>
                    {/* <tfoot><tr><td className="fs-6" colSpan={10}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot> */}
                </table>
            </Loadable>

            <AddGroupsModal
                showSignal={showModalAddGroupsSignal}
                cancelAdd={closeModalAddGroups}
                selectedGroups={groupsToAddToUserSignal.value}
                userGroupsSignal={userGroupsSignal.value}
                businessEntity={beId}
                handleSubmit={addGroupToUser}
            ></AddGroupsModal>
            {
                businessEntitiesLoadedSignal.value && (
                    <AddRolesModal
                        showSignal={showModalAddRolesSignal}
                        cancelAdd={closeModalAddRoles}
                        businessEntity={currentBusinessEntitySignal.value}
                        userId={userId}
                    ></AddRolesModal>
                )
            }
        </>
    )
}